/**
 * SkipLink — Accessible "Skip to main content" link.
 *
 * Visually hidden until it receives keyboard focus, at which point it
 * jumps into view and lets the user bypass repeated navigation. Two
 * demos (hydra-cut and SeamBridgeMonograph) had their own copies; this
 * version factors out the CSS-in-JS so any demo can drop it in
 * without ferrying styles across files.
 *
 * Activation focuses and scrolls to the target element directly rather
 * than navigating to `#<targetId>`. That matters when the surrounding
 * shell stores route state in `location.hash` (e.g. `#panel=<id>`):
 * a default `<a href="#main">` would clobber the hash and reset the
 * active panel. The `href` is still emitted for assistive tech and
 * fallback behaviour when JavaScript is disabled.
 *
 * Render this as the first focusable child of your root layout, with
 * `targetId` pointing at the `id` of the main landmark element
 * (typically `<main id="main" tabIndex={-1}>` so focus can land on it).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";

interface SkipLinkProps {
  readonly targetId: string;
  readonly label?: string;
  /** Optional class to override or extend the focus-visible styling. */
  readonly className?: string;
  /** Optional style overrides; merged after the default focus styling. */
  readonly style?: React.CSSProperties;
}

const HIDDEN_STYLE: React.CSSProperties = {
  position: "absolute",
  left: "-9999px",
  top: "0.5rem",
  padding: "0.5rem 0.75rem",
  background: "#ffffff",
  color: "#0f172a",
  border: "1px solid #cbd5e1",
  borderRadius: "0.5rem",
  boxShadow: "0 1px 3px rgba(15,23,42,0.12)",
  fontSize: "0.75rem",
  fontWeight: 600,
  textDecoration: "none",
  zIndex: 1000,
};

const FOCUSED_STYLE: React.CSSProperties = {
  ...HIDDEN_STYLE,
  left: "1rem",
  outline: "none",
  boxShadow: "0 0 0 3px rgba(37,99,235,0.45)",
};

export function SkipLink({
  targetId,
  label = "Skip to main content",
  className,
  style,
}: SkipLinkProps): React.JSX.Element {
  const [focused, setFocused] = React.useState(false);
  const baseStyle = focused ? FOCUSED_STYLE : HIDDEN_STYLE;

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const target = document.getElementById(targetId);
      if (target === null) {
        return;
      }
      event.preventDefault();

      const hadTabIndex = target.hasAttribute("tabindex");
      if (!hadTabIndex) {
        target.setAttribute("tabindex", "-1");
        target.addEventListener(
          "blur",
          () => {
            target.removeAttribute("tabindex");
          },
          { once: true },
        );
      }

      target.focus({ preventScroll: true });
      const scrollIntoView = (
        target as {
          scrollIntoView?: (arg?: boolean | ScrollIntoViewOptions) => void;
        }
      ).scrollIntoView;
      if (scrollIntoView !== undefined) {
        scrollIntoView.call(target, { block: "start", behavior: "auto" });
      }
    },
    [targetId],
  );

  return (
    <a
      href={`#${targetId}`}
      className={className}
      style={{ ...baseStyle, ...style }}
      onClick={handleClick}
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={() => {
        setFocused(false);
      }}
    >
      {label}
    </a>
  );
}
